import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { PageHead, PageToolbar, PageWithAside, PendingDecision } from '@/components/editorial';
import { ErrorStateCard } from '@/components/ui/ErrorStateCard';
import { cn, localizedApiErrorMessage } from '@/lib/utils';
import { COARSE_HIT_28 } from '@/lib/touch-target';
import { NAV_KEY } from '@/i18n/keys/nav';
import { formatDateRange, formatMonthYear, weekStartFor } from '@/i18n/intl';
import { AddAppointmentModal } from '@/components/calendar/AddAppointmentModal';
import { AppointmentDetailDialog } from '@/components/calendar/AppointmentDetailDialog';
import type { CreateAppointmentRequest, MedicalAppointment } from '@/types/api';
import { useAuth } from '@/contexts/AuthContext';
import { useCompanionName } from '@/hooks/useCompanionName';
import { api } from '@/services/api';
import {
  CalendarFilterPanel,
  AgendaView,
  WeekView,
  MonthView,
} from '@/components/calendar/CalendarViews';
import {
  CalendarStevensCallout,
  buildCalendarPatterns,
} from '@/components/calendar/CalendarStevensCallout';
import {
  appointmentToEvent,
  eventMatchesSearch,
  loadCalendarView,
  loadHiddenCats,
  medicationDoseEvents,
  persistCalendarView,
  persistHiddenCats,
  stevensSuggestedEvents,
  taskToEvent,
  type CalendarView,
  type EventCategory,
  type UnifiedEvent,
} from '@/lib/calendar-display';
import { visibleParentTasks } from '@/lib/task-display';
import { navigateToStevens } from '@/lib/chat-navigation';
import { useDashboardMode } from '@/hooks/useDashboardMode';
import { useTranslation } from 'react-i18next';
import { useMsg } from '@/i18n/msg';
const VIEW_OPTIONS = [
  { value: 'agenda', labelKey: 'calendar:page.view.agenda' },
  { value: 'week',   labelKey: 'calendar:page.view.week' },
  { value: 'month',  labelKey: 'calendar:page.view.month' },
] as const satisfies ReadonlyArray<{ value: CalendarView; labelKey: string }>;

/** The status-change toast, one whole title and body per status. */
/** The month arrows' drawn box: 28 px on every pointer (#2603 keeps it). */
const CALENDAR_ARROW =
  'h-7 w-7 inline-grid place-items-center rounded-md border border-border text-stevens-ink-3 hover:bg-muted hover:text-foreground transition-colors';

const STATUS_TOAST = {
  attended:  { title: 'calendar:page.toast.status.attended',  description: 'calendar:page.toast.status.attendedBody' },
  cancelled: { title: 'calendar:page.toast.status.cancelled', description: 'calendar:page.toast.status.cancelledBody' },
  missed:    { title: 'calendar:page.toast.status.missed',    description: 'calendar:page.toast.status.missedBody' },
  scheduled: { title: 'calendar:page.toast.status.scheduled', description: 'calendar:page.toast.status.scheduledBody' },
} as const satisfies Record<MedicalAppointment['status'], { title: string; description: string }>;

export function CalendarPage() {
  const { t, i18n } = useTranslation(['calendar', 'nav', 'common']);
  const resolve = useMsg();
  const language = i18n.resolvedLanguage ?? i18n.language;
  // The week starts where the reader's locale starts it; en stays Sunday (R3-B5).
  const weekStartsOn = weekStartFor(i18n.language);
  // R3-B11: the server's English reaches only an English reader.
  const errorText = (e: unknown) => localizedApiErrorMessage(e, t, i18n.language) || t('page.toast.pleaseTryAgain');
  const navigate = useNavigate();
  const companionName = useCompanionName();
  const { user } = useAuth();
  const isParent = user?.role === 'parent';
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Appointments live here (2026-09-09) ──
  //
  // The Health page's appointments tab was the third mention of the same
  // records (the calendar and the Today card already mirrored them), so the
  // calendar is their home now: "New event → Appointment" opens the modal in
  // place, clicking an appointment opens its detail (edit / status / delete),
  // and `?id=` deep-links it — the Today card and the old `/medical?tab=
  // appointments&id=` redirect both land here.
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<MedicalAppointment | null>(null);
  const detailId = searchParams.get('id');

  const openDetail = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('id', id);
    setSearchParams(next, { replace: true });
  };
  const closeDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next, { replace: true });
  };

  const [view, setView] = useState<CalendarView>(() => loadCalendarView());
  const [anchor, setAnchor] = useState<Date>(new Date());
  const [hiddenCats, setHiddenCats] = useState<Set<EventCategory>>(() => loadHiddenCats());
  // The toolbar search. Not persisted: a query the parent cannot see on
  // return would filter the calendar invisibly (spec §5).
  const [search, setSearch] = useState('');

  // #649: the retired "calendar share settings" dialog persisted a consent
  // record that governed nothing — and its shareMedications:false default hid
  // the parent's own medication events. Nothing reads the key anymore; drop
  // any stale copy so it can't be mistaken for a live setting.
  useEffect(() => { localStorage.removeItem('calendar_share_settings'); }, []);

  // Determine the date range for the active view
  const range = useMemo(() => {
    if (view === 'agenda') {
      const start = startOfDay(anchor);
      return { start, end: endOfDay(new Date(start.getTime() + 18 * 86_400_000)) };
    }
    if (view === 'week') {
      return {
        start: startOfWeek(anchor, { weekStartsOn }),
        end:   endOfWeek(anchor, { weekStartsOn }),
      };
    }
    return { start: startOfMonth(anchor), end: endOfMonth(anchor) };
  }, [view, anchor, weekStartsOn]);

  // Data.
  //
  // Deliberate asymmetry (#849, recorded rather than converged): tasks are
  // RANGE-scoped server-side while appointments and medications are fetched
  // whole. Tasks are the one unbounded collection — recurring series fan out
  // into N documents — so the range param keeps that payload finite; the
  // appointment and medication sets stay small, and fetching them whole is
  // what lets month/week navigation and the dose fanout work without a
  // refetch per range. Converging would mean either range params on the
  // medical routes (payloads that don't need bounding) or fetch-all tasks
  // (a payload that does).
  //
  // The key sits under the `tasks` prefix (#2432): /tasks invalidates
  // `['tasks']` after every write, and a separate `calendar-tasks` key kept a
  // new task off the calendar for the app's 5-minute staleTime.
  const { data: tasksData, isLoading: tasksLoading, error: tasksError, refetch } = useQuery({
    queryKey: ['tasks', 'calendar', range.start.toISOString().slice(0, 10), range.end.toISOString().slice(0, 10)],
    queryFn: () => api.tasks.getAll({
      start_date: range.start.toISOString(),
      end_date: range.end.toISOString(),
    }),
  });

  // Fetch all appointments (past + future) so the calendar can show history
  // when the user navigates to prior weeks/months. Key shared with /medical
  // and the dashboard (#849): one cache entry instead of three variants, and
  // HealthPage's appointment CRUD invalidations reach this page too.
  const { data: apptsData } = useQuery({
    queryKey: ['medical', 'appointments'],
    queryFn: () => api.medical.appointments.getAll(),
  });

  const { data: medsData } = useQuery({
    queryKey: ['medications'],
    queryFn: () => api.medical.medications.getAll(),
  });

  const invalidateAppointments = () =>
    queryClient.invalidateQueries({ queryKey: ['medical', 'appointments'] });

  const createAppointment = useMutation({
    mutationFn: (data: CreateAppointmentRequest) => api.medical.appointments.create(data),
    onSuccess: () => {
      invalidateAppointments();
      setShowAppointmentDialog(false);
      toast({ title: t('page.toast.created'), description: t('page.toast.createdBody') });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('page.toast.createFailed'), description: errorText(error) });
    },
  });

  const updateAppointment = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateAppointmentRequest> }) =>
      api.medical.appointments.update(id, data),
    onSuccess: () => {
      invalidateAppointments();
      setShowAppointmentDialog(false);
      setEditingAppointment(null);
      toast({ title: t('page.toast.updated'), description: t('page.toast.updatedBody') });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('page.toast.updateFailed'), description: errorText(error) });
    },
  });

  const updateAppointmentStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: MedicalAppointment['status'] }) =>
      api.medical.appointments.updateStatus(id, status),
    onSuccess: (_data, { status }) => {
      invalidateAppointments();
      // One title and one body per status — whole sentences, not a ternary of fragments.
      const shape = STATUS_TOAST[status as keyof typeof STATUS_TOAST] ?? STATUS_TOAST.scheduled;
      toast({ title: t(shape.title), description: t(shape.description) });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('page.toast.statusFailed'), description: errorText(error) });
    },
  });

  // True delete lives behind the detail view's in-app confirm. No undo toast:
  // re-creating an appointment would mint a new id, so questions linked to
  // the old one would stay orphaned — a false "Undo" promise (#847 honesty).
  // The recoverable "remove" is cancelling (D-C).
  const deleteAppointment = useMutation({
    mutationFn: (appt: MedicalAppointment) => api.medical.appointments.delete(appt._id),
    onSuccess: () => {
      invalidateAppointments();
      closeDetail();
      toast({ title: t('page.toast.deleted'), description: t('page.toast.deletedBody') });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('page.toast.deleteFailed'), description: errorText(error) });
    },
  });

  // #693: same memorial gate as /tasks and the dashboard — applied to EVERY
  // event class this page renders, not only to tasks.
  //
  // The first pass gated the task list and left the other three classes
  // untouched, which produced the worst possible screen: a bereaved family
  // lost the row "Pick up Lila's prescription" while "Chemo — infusion #6"
  // kept its slot on next Tuesday, a medication dose marker painted every day
  // in the visible range, and `stevensSuggestedEvents` derived a prep event on
  // top of the appointment. A half-switched calendar is worse than an ungated
  // one: the family can see that something was decided, and what was decided
  // is that the appointment still stands.
  //
  // DashboardPage already suppresses this class
  // (`hasActiveMeds={mode !== 'memorial' && hasActiveMeds}`), so before this
  // change two parent surfaces disagreed and the worse one was the one still
  // on screen.
  const { mode, isMemorial, isLoading: modeLoading } = useDashboardMode();

  // Treatment-era classes are also withheld for the render where the family
  // record is still in flight. `resolveDashboardMode` fails safe to 'active',
  // so without this a bereaved family's chemo appointment would paint and then
  // be retracted — a flash of exactly the content the gate exists to remove.
  const hideTreatmentEvents = modeLoading || isMemorial;

  const rawTasks = tasksData?.data ?? [];
  const tasks = useMemo(
    () => (modeLoading ? [] : visibleParentTasks(rawTasks, mode)),
    [rawTasks, mode, modeLoading],
  );
  // Appointments and medications are treatment records whole — there is no
  // per-row classifier to run the way `visibleParentTasks` runs one, because
  // there is no such thing as a non-treatment appointment on this surface.
  const appointments = useMemo(
    () => (hideTreatmentEvents ? [] : apptsData?.data ?? []),
    [apptsData, hideTreatmentEvents],
  );
  const medications = useMemo(
    () => (hideTreatmentEvents ? [] : medsData?.data ?? []),
    [medsData, hideTreatmentEvents],
  );

  // `?id=` resolves against the GATED list, so a memorial family's deep link
  // opens nothing — the same silence the grid keeps. A stale id also opens
  // nothing rather than erroring (D-F deep-link degrade).
  const detailAppt = useMemo(
    () => (detailId ? appointments.find((a) => a._id === detailId) : undefined),
    [appointments, detailId],
  );

  // Build the unified event list for the visible range. All four sources are
  // already memorial-gated above — `tasks` per row, `appointments` and
  // `medications` whole — so the dose markers and the derived Stevens prep
  // events, which are functions of those two arrays, cannot outlive the gate.
  const allEvents: UnifiedEvent[] = useMemo(() => {
    const out: UnifiedEvent[] = [];
    for (const t of tasks) {
      const ev = taskToEvent(t);
      if (ev) out.push(ev);
    }
    for (const a of appointments) {
      out.push(appointmentToEvent(a));
    }
    out.push(...medicationDoseEvents(medications, range.start, endOfDay(range.end)));
    out.push(...stevensSuggestedEvents(appointments));
    return out;
  }, [tasks, appointments, medications, range.start, range.end]);

  // Apply the category filter, then the search. `hiddenCats` is the filter
  // popover's state, and in memorial mode the filter icon is not rendered — so
  // a family who had toggled the Task chip off before their stage changed
  // would otherwise be left with an empty calendar and no control to bring it
  // back. The filter is dropped with the control it belongs to rather than
  // surviving it invisibly. Search applies in every view and every mode; the
  // toolbar clears it whenever the field closes.
  const visibleEvents = useMemo(
    () =>
      allEvents.filter(
        (e) => (isMemorial || !hiddenCats.has(e.category)) && eventMatchesSearch(e, search),
      ),
    [allEvents, hiddenCats, isMemorial, search],
  );

  // Persist view + hidden cats
  useEffect(() => { persistCalendarView(view); }, [view]);
  useEffect(() => { persistHiddenCats(hiddenCats); }, [hiddenCats]);

  // Stevens patterns
  const patterns = useMemo(
    () =>
      buildCalendarPatterns({
        appointments,
        tasks,
        // ?prompt= was a dead param — ChatPage reads only prefill/autoopen (+
        // conversationId/voice/mode/new); the shared helper sends the working combo.
        // R3-B2: the prompt is sent in the UI language.
        onTalkToStevens: (prompt) => navigateToStevens(navigate, resolve(prompt)),
        onCreateTask: () => navigate('/tasks?action=create'),
        companionName,
        locale: language,
      }),
    [appointments, tasks, navigate, companionName, resolve, language],
  );

  // Through Intl (R3-B5): "September 2026" · "septiembre de 2026", and a week
  // across two months is a formatRange ("Sep – Oct 2026").
  const headerLabel = useMemo(() => {
    if (view === 'week') {
      const ws = startOfWeek(anchor, { weekStartsOn });
      const we = endOfWeek(anchor, { weekStartsOn });
      if (ws.getMonth() !== we.getMonth()) {
        return formatDateRange(ws, we, { locale: language, preset: { month: 'short', year: 'numeric' } });
      }
    }
    return formatMonthYear(anchor, { locale: language });
  }, [view, anchor, weekStartsOn, language]);

  const goPrev = () => {
    if (view === 'month') setAnchor(subMonths(anchor, 1));
    else if (view === 'week') setAnchor(subWeeks(anchor, 1));
    else setAnchor(new Date(anchor.getTime() - 14 * 86_400_000));
  };
  const goNext = () => {
    if (view === 'month') setAnchor(addMonths(anchor, 1));
    else if (view === 'week') setAnchor(addWeeks(anchor, 1));
    else setAnchor(new Date(anchor.getTime() + 14 * 86_400_000));
  };
  const goToday = () => setAnchor(new Date());

  const toggleCat = (c: EventCategory) => {
    setHiddenCats((cur) => {
      const next = new Set(cur);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };

  const handleEventClick = (e: UnifiedEvent) => {
    // A task event lands on its own card — /tasks/:id exists and highlights
    // it (spec §5, C8) — rather than on the bare list.
    if (e.source === 'task') navigate(e.taskId ? `/tasks/${e.taskId}` : '/tasks');
    else if (e.source === 'appointment' && e.appointmentId) openDetail(e.appointmentId);
    else if (e.source === 'medication') navigate('/medical?tab=medications');
    // Stevens-suggestions: no-op; the callout handles its own actions
  };

  // The same create flow as the + menu's appointment item (#2532). Memorial
  // mode hides appointments, so the empty state offers nothing there.
  const openNewAppointment = () => { setEditingAppointment(null); setShowAppointmentDialog(true); };
  const onAddAppointment = isParent && !hideTreatmentEvents ? openNewAppointment : undefined;

  const handleSelectDay = (day: Date) => {
    setAnchor(day);
    setView('agenda');
  };

  return (
    // One column, unless the companion has something to say — then her card
    // takes a right column that opens at `xl`, not `lg` as Health's does: the
    // month grid needs the width a 300px column at 1024px would not leave it
    // (`(?)` calendar-companion-column). Memorial gating stays page-level —
    // `patterns` is built from the GATED appointment and task lists, so a
    // bereaved family's aside carries no treatment-era remark.
    <PageWithAside
      asideAt="xl"
      aside={isParent && patterns.length > 0 ? (
        <>
          <CalendarStevensCallout patterns={patterns} />
          <PendingDecision id="calendar-companion-column" side="bottom" className="mt-2" />
        </>
      ) : null}
    >
    <div className="space-y-6">
      {/* Header (declutter 2026-09-10, spec §5; headline removed the same
          day, DECISIONS "The headline goes"). PageHead renders the eyebrow as
          the H1 and nothing else; beneath it ONE toolbar row — `+ · search ·
          filter` on the left, the date navigation and view switch in the
          primitive's `end` slot on the right, wrapping under on a phone.
          Nothing in the header is a number: the "N urgent · N medical this
          week" strip restated the grid beneath it and is gone (C1). */}
      <header className="pb-3.5 border-b border-border/60 space-y-3">
        <PageHead eyebrowKey={NAV_KEY.calendar} />

        <PageToolbar
          label={t(NAV_KEY.calendar)}
          // C3: the + lists every create action, so the picker dialog is gone
          // (2026-09-10 review). In memorial mode the appointment item is the
          // same invitation the gate above removed from the grid, so only the
          // task remains and the + runs it directly.
          add={isParent ? {
            items: hideTreatmentEvents
              ? [{ label: t('page.newTask'), onSelect: () => navigate('/tasks?action=create') }]
              : [
                  { label: t('page.newTask'), onSelect: () => navigate('/tasks?action=create') },
                  { label: t('page.newAppointment'), onSelect: openNewAppointment },
                ],
          } : undefined}
          search={{ value: search, onChange: setSearch, placeholder: t('page.searchPlaceholder') }}
          // The filter's four toggles — Medical, Task, Medication,
          // {companionName} — are each a claim that the class exists on this
          // calendar. In memorial mode three of them can never match anything,
          // and a filter over the one remaining class filters nothing, so the
          // icon goes rather than opening onto a single dead chip.
          filter={isMemorial ? undefined : {
            active: hiddenCats.size > 0,
            label: t('page.filterLabel'),
            panel: (
              <>
                <CalendarFilterPanel hidden={hiddenCats} onToggle={toggleCat} />
                {/* A pending-decision caption for TRF, not family copy: it stays English (R3-B20). */}
                <p className="flex items-center gap-1.5 text-caption text-stevens-ink-3" lang="en">
                  Views are a switch, not sections
                  <PendingDecision id="calendar-views-as-tabs" side="bottom" />
                </p>
              </>
            ),
          }}
          end={
            <>
              {/* #2603 (D-2420): the 28 px arrows reach 44 px on a coarse
                  pointer. `isolate` makes this group the stacking context, so
                  the -z-10 hit areas sit under the month label between them;
                  the toolbar's 8 px end gap keeps Next's reach off Today.
                  #2643 (b): at the 6 px gap a tap 20 px from an arrow's centre
                  (6 px past its edge) landed on the month label, which paints
                  over the -z-10 reach. A coarse pointer gets an 8 px gap, so
                  that point falls in the gap, on the arrow's own reach. */}
              <div className="isolate inline-flex items-center gap-1.5 [@media(pointer:coarse)]:gap-2">
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label={t('common:actions.previous')}
                  className={cn(CALENDAR_ARROW, COARSE_HIT_28)}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <span className="px-2.5 text-card-title text-foreground font-serif">
                  {headerLabel}
                </span>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label={t('common:actions.next')}
                  className={cn(CALENDAR_ARROW, COARSE_HIT_28)}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>

              <Button variant="outline" size="sm" onClick={goToday} className="h-8">
                {t('views.today')}
              </Button>

              {/* A view that is not a URL is not a tab (design-grammar §4.5),
                  so this stays a segmented control — `(?)` calendar-views-as-tabs
                  sits in the filter popover. */}
              <div className="inline-flex items-center gap-0.5 rounded-md bg-muted p-0.5">
                {VIEW_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setView(o.value)}
                    className={
                      view === o.value
                        ? 'rounded bg-background px-3 py-1 text-caption font-medium text-foreground shadow-sm'
                        : 'rounded px-3 py-1 text-caption font-medium text-stevens-ink-3 hover:text-foreground transition-colors'
                    }
                  >
                    {t(o.labelKey)}
                  </button>
                ))}
              </div>
            </>
          }
        />
      </header>

      {/* View body */}
      {tasksLoading || modeLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-stevens-ink-3" />
        </div>
      ) : tasksError ? (
        <ErrorStateCard message={t('page.loadError')} />
      ) : view === 'agenda' ? (
        <AgendaView
          events={visibleEvents}
          rangeStart={anchor}
          onEventClick={handleEventClick}
          todayNote={null}
          onAddAppointment={onAddAppointment}
        />
      ) : view === 'week' ? (
        <WeekView events={visibleEvents} weekAround={anchor} onEventClick={handleEventClick} onAddAppointment={onAddAppointment} />
      ) : (
        <MonthView
          events={visibleEvents}
          monthAround={anchor}
          onSelectDay={handleSelectDay}
          onEventClick={handleEventClick}
        />
      )}

      <AddAppointmentModal
        open={showAppointmentDialog}
        onOpenChange={(o) => { setShowAppointmentDialog(o); if (!o) setEditingAppointment(null); }}
        editing={editingAppointment}
        pastAppointments={appointments}
        isSubmitting={createAppointment.isPending || updateAppointment.isPending}
        onSubmit={(data) => {
          if (editingAppointment) {
            updateAppointment.mutate({ id: editingAppointment._id, data });
          } else {
            createAppointment.mutate(data);
          }
        }}
      />

      {detailAppt && (
        <AppointmentDetailDialog
          open
          onOpenChange={(o) => { if (!o) closeDetail(); }}
          appt={detailAppt}
          onEdit={(a) => { setEditingAppointment(a); setShowAppointmentDialog(true); }}
          onSetStatus={(a, status) => updateAppointmentStatus.mutate({ id: a._id, status })}
          onDelete={(a) => deleteAppointment.mutate(a)}
          isMutating={updateAppointmentStatus.isPending || deleteAppointment.isPending}
        />
      )}

      {/* refetch hook to keep TS happy if used externally */}
      <span hidden aria-hidden onClick={() => refetch()} />
    </div>
    </PageWithAside>
  );
}
